<template>
  <div v-if="!errorPage">
  <div v-if="activeTab === 'General Settings'">
    <GeneralSettings />

  </div>
  <div v-else class="py-6 w-full h-full px-3">
    <h1 class="text-xl text-left font-bold flex mb-0 -mt-2">
      Pay Group
    </h1>
    <div class="w-full h-0 p-0 mb-4" style="border: 0.2px solid #d6dbe4" />

    <!-- Custom Pay Group Tabs -->
    <div class="mb-4">
      <PaySettingsTabs
        :tabs="paySettingsTabs"
        :active-tab="currentPaySettingId"
        @tab-change="handlePaySettingChange"
        @edit-tab="editPaySetting"
        @add-new-tab="addNewPaySetting"
        @next-tab="handleNextTab"
        @previous-tab="handlePreviousTab"
      />
    </div>

    <Card>
      <Tabs
        :key="`${currentPaySettingId}-${tabKey}`"
        class="mt-2 mx-2 text-sm"
        style="font-size: 12px !important;"
        :tabs="tabsItems"
        :active-tab="activeTab"
        @currentTab="activeTab = $event"
        :border="true"
      />
    </Card>

    <!-- Main Content -->
    <div>
      <template v-if="!isIndependentContractor">
        <Earnings
          v-if="activeTab === 'Earnings'"
          :org-pay-setting-id="currentPaySettingId"
        />
        <Deductions
          v-if="activeTab === 'Deductions'"
          :org-pay-setting-id="currentPaySettingId"
        />
        <Compliance
          v-if="activeTab === 'Compliance (Pension, PAYE, NHF, NHIS)'"
          :org-pay-setting-id="currentPaySettingId"
        />
      </template>

      <!-- Independent Contractor Components -->
      <template v-else>
        <IndependentContractorEarnings
          v-if="activeTab === 'Earnings'"
          :org-pay-setting-id="currentPaySettingId"
        />
        <IndependentContractorCompliance
          v-if="activeTab === 'Compliance (WHT)'"
          :org-pay-setting-id="currentPaySettingId"
          :is-active="activeTab === 'Compliance (WHT)'"
        />
      </template>
    </div>

    <!-- Add New Pay Group Modal -->
    <AddNewPaySettingsModal
      v-if="showAddNewModal"
      :existing-settings="isEditingMode ? allPaySettings : paySettingsTabs"
      :editing-setting="editingPaySetting"
      :default-criteria="defaultCriteria"
      :is-editing-mode="isEditingMode"
      @close="handleCloseModal"
      @save="handleSavePaySetting"
      @delete="handleDeletePaySetting"
    />
  </div>
</div>

  <div v-else>
    <ErrorPage />
  </div>
</template>

<script>
import Tabs from "@scelloo/cloudenly-ui/src/components/tab";
import Card from "@/components/Card";
import ErrorPage from "@/modules/Admin/error403";
import Earnings from "./Earnings";
import Deductions from "./Deductions";
import Compliance from "./Compliance";
import PaySettingsTabs from "./PaySettingsTabs";
import AddNewPaySettingsModal from "./AddNewPaySettingsModal";
import GeneralSettings from "./generalSettings";
import IndependentContractorEarnings from "./IndependentContractor/Earnings";
import IndependentContractorCompliance from "./IndependentContractor/Compliance";

export default {
  components: {
    Tabs,
    Card,
    Earnings,
    Deductions,
    Compliance,
    PaySettingsTabs,
    AddNewPaySettingsModal,
    GeneralSettings,
    IndependentContractorEarnings,
    IndependentContractorCompliance,
    ErrorPage
  },
  data() {
    return {
      errorPage: false,
      activeTab: this.$route.query.payType || "Pay Schedule",
      currentPaySettingId: this.$store.state.paySettingsActiveIds.ACTIVE || null,
      tabKey: 0,
      showDescription: false,
      enabledView: false,
      kpaModal: false,
      showAddNewModal: false,
      loadingPaySettings: false,
      editingPaySetting: null,
      defaultCriteria: null,
      isEditingMode: false,
      allPaySettings: [],
      baseTabsItems: [
        "Earnings",
        "Deductions",
        "Compliance (Pension, PAYE, NHF, NHIS)",
      ],
      baseIndependentContractorTabsItems: [
        "Earnings",
        "Compliance (WHT)",
      ],
      paySettingsTabs: []
    };
  },
  computed: {
    isIndependentContractor() {
      const currentSetting = this.paySettingsTabs.find(t => t.id === this.currentPaySettingId);
      return currentSetting?.name === 'Independent Contractor';
    },
    tabsItems() {
      if (this.isIndependentContractor) {
        return this.baseIndependentContractorTabsItems;
      }
      return this.baseTabsItems;
    }
  },
  methods: {
    sortPaySettingsTabs(tabs) {
      const independentContractorIndex = tabs.findIndex(tab => tab.name === 'Independent Contractor');
      if (independentContractorIndex !== -1) {
        const independentContractor = tabs.splice(independentContractorIndex, 1)[0];
        tabs.push(independentContractor);
      }
      return tabs;
    },
    async loadPaySettings(preserveState = false) {
      this.loadingPaySettings = true;

      // Store current state before reloading if preserving state
      const previousPaySettingId = preserveState ? this.currentPaySettingId : null;
      const previousActiveTab = preserveState ? this.activeTab : null;

      try {
        const response = await this.$_getPaySettings();
        if (response.data && response.data.paySettings && response.data.paySettings.length > 0) {
          this.paySettingsTabs = response.data.paySettings.map(ps => {
            // Ensure groupIds is properly populated
            let groupIds = ps.groupIds || [];

            // If groupIds is empty but groupValues exists, extract IDs from groupValues
            // This handles cases where backend returns groupValues but empty groupIds
            if (groupIds.length === 0 && ps.groupValues && Array.isArray(ps.groupValues) && ps.groupValues.length > 0) {
              groupIds = ps.groupValues.map(item => {
                // Handle both object with id property and direct string/uuid values
                if (typeof item === 'object' && item !== null) {
                  return item.id || item;
                }
                return item;
              }).filter(Boolean);
            }

            // Normalize groupIds to ensure they're strings (for consistency)
            if (Array.isArray(groupIds) && groupIds.length > 0) {
              groupIds = groupIds.map(id => {
                if (typeof id === 'object' && id !== null && id.id) {
                  return id.id;
                }
                return String(id);
              });
            }

            return {
              id: ps.id,
              name: ps.name,
              group: ps.group,
              groupIds,
              groupValues: ps.groupValues || [],
              criteriaId: this.mapGroupToCriteriaId(ps.group)
            };
          });

          // Ensure Independent Contractor is always last
          this.sortPaySettingsTabs(this.paySettingsTabs);

          if (this.paySettingsTabs.length > 0 && !this.defaultCriteria) {
            this.defaultCriteria = this.paySettingsTabs[0].criteriaId || 'employmentType';
          }

          const isIndependentContractorRequest = this.$route.query.payType === 'independent-contractor';
          const orgPaySettingIdFromQuery = this.$route.query.orgPaySettingId;

          if (orgPaySettingIdFromQuery) {
            const matchingTab = this.paySettingsTabs.find(tab => tab.id === orgPaySettingIdFromQuery);
            if (matchingTab && this.currentPaySettingId !== orgPaySettingIdFromQuery) {
              this.currentPaySettingId = orgPaySettingIdFromQuery;
              this.$store.commit('SET_PAY_SETTINGS_ID', { key: 'ACTIVE', id: this.currentPaySettingId });
              if (this.$route.query.payType) {
                this.activeTab = this.$route.query.payType;
              }
              this.$nextTick(() => {
                this.tabKey += 1;
              });
            }
          } else if (isIndependentContractorRequest) {
            const independentContractorTab = this.paySettingsTabs.find(tab => tab.name === 'Independent Contractor');
            if (independentContractorTab) {
              this.currentPaySettingId = independentContractorTab.id;
              this.$store.commit('SET_PAY_SETTINGS_ID', { key: 'ACTIVE', id: this.currentPaySettingId });
              this.activeTab = 'Earnings';
              this.$nextTick(() => {
                this.tabKey += 1;
              });
            } else if (!this.currentPaySettingId) {
              // Fallback to first tab if Independent Contractor not found
              this.currentPaySettingId = this.paySettingsTabs[0].id;
              this.$store.commit('SET_PAY_SETTINGS_ID', { key: 'ACTIVE', id: this.currentPaySettingId });
            }
          } else if (preserveState && previousPaySettingId) {
            // Try to restore the previous pay group if it still exists
            const previousSettingExists = this.paySettingsTabs.find(tab => tab.id === previousPaySettingId);
            if (previousSettingExists) {
              this.currentPaySettingId = previousPaySettingId;
              this.$store.commit('SET_PAY_SETTINGS_ID', { key: 'ACTIVE', id: this.currentPaySettingId });
              // Restore the previous active tab if it's valid for this pay group
              if (previousActiveTab) {
                const currentSetting = this.paySettingsTabs.find(t => t.id === this.currentPaySettingId);
                const isIndependentContractor = currentSetting?.name === 'Independent Contractor';
                const validTabs = isIndependentContractor ? this.baseIndependentContractorTabsItems : this.baseTabsItems;
                if (validTabs.includes(previousActiveTab)) {
                  this.activeTab = previousActiveTab;
                } else {
                  this.activeTab = validTabs[0] || "Earnings";
                }
              }
              this.$nextTick(() => {
                this.tabKey += 1;
              });
            } else if (this.paySettingsTabs.length > 0) {
              // Previous setting doesn't exist, fall back to first one
              this.currentPaySettingId = this.paySettingsTabs[0].id;
              this.$store.commit('SET_PAY_SETTINGS_ID', { key: 'ACTIVE', id: this.currentPaySettingId });
              this.activeTab = this.tabsItems[0] || "Earnings";
            }
          } else if (this.paySettingsTabs.length > 0 && !this.currentPaySettingId) {
            this.currentPaySettingId = this.paySettingsTabs[0].id;
            this.$store.commit('SET_PAY_SETTINGS_ID', { key: 'ACTIVE', id: this.currentPaySettingId });
          }
        } else {
          this.paySettingsTabs = [];
        }
      } catch (error) {
        this.paySettingsTabs = [];
      } finally {
        this.loadingPaySettings = false;
      }
    },
    handleNextTab(tabId) {
      this.handlePaySettingChange(tabId);
    },
    handlePreviousTab(tabId) {
      this.handlePaySettingChange(tabId);
    },
    mapGroupToCriteriaId(group) {
      const mapping = {
        'employment-type': 'employmentType',
        'job-level': 'jobLevel',
        'organizational-unit': 'organizationalUnit',
        'location': 'location'
      };
      return mapping[group] || 'employmentType';
    },
    handlePaySettingChange(tabId) {
      // Reset active tab to the first one (Earnings) immediately
      this.activeTab = this.tabsItems[0] || "Earnings";

      // Update the pay group
      this.currentPaySettingId = tabId;
      this.$store.commit('SET_PAY_SETTINGS_ID', { key: 'ACTIVE', id: tabId });

      // Force Tabs component to re-render with new activeTab value
      this.$nextTick(() => {
        this.tabKey += 1;
      });
    },
    editPaySetting(tab) {
      // Get full pay group data for editing
      const selectedSetting = this.paySettingsTabs.find(ps => ps.id === tab.id);

      if (!selectedSetting) {
        return;
      }

      // Filter all pay groups that share the same group/criteria
      // But always include Independent Contractor regardless of group
      this.allPaySettings = this.paySettingsTabs.filter(ps =>
        ps.group === selectedSetting.group || ps.name === 'Independent Contractor'
      );

      this.editingPaySetting = selectedSetting;
      this.isEditingMode = true;
      this.showAddNewModal = true;
    },
    async addNewPaySetting() {
      // Refresh pay groups before opening modal to ensure fresh data
      await this.loadPaySettings(true);

      this.editingPaySetting = null;
      this.allPaySettings = [];
      this.isEditingMode = false;
      this.showAddNewModal = true;
    },
    handleCloseModal() {
      this.showAddNewModal = false;
      this.editingPaySetting = null;
      this.isEditingMode = false;
      this.allPaySettings = [];

      // Refresh pay groups after modal is closed to ensure data is up to date
      // Use $nextTick to ensure modal is fully closed before refreshing
      this.$nextTick(() => {
        this.loadPaySettings(true);
      });
    },
    async handleSavePaySetting(paySettingData) {
      try {
        let payload;

        // Handle Independent Contractor (object with name only)
        if (!Array.isArray(paySettingData) && paySettingData.name === 'Independent Contractor') {
          payload = {
            paySettings: [{
              name: 'Independent Contractor'
            }]
          };
        }
        // Handle array of pay groups (step 2 scenario)
        else if (Array.isArray(paySettingData)) {
          payload = {
            paySettings: paySettingData.map(setting => ({
              id: setting.id || null,
              name: setting.name,
              group: setting.group,
              groupIds: setting.groupIds || []
            }))
          };
        }
        // Handle single pay group update
        else {
          payload = {
            paySettings: [{
              name: paySettingData.name || 'pay group 1',
            }]
          };

          if (paySettingData.id) {
            payload.paySettings[0].id = paySettingData.id;
          }
        }

        await this.$_savePaySettings(payload);

        const settingName = Array.isArray(paySettingData)
          ? `${paySettingData.length} pay group(s)`
          : paySettingData.name || 'Pay group';

        this.$toasted.success(`Saved ${settingName}`, { duration: 2000 });
        this.showAddNewModal = false;

        await this.loadPaySettings(true);
      } catch (error) {
        const errorMessage = error.response?.data?.message || error
        this.$toasted.error(errorMessage, { duration: 5000 });
      }
    },
    async handleDeletePaySetting(paySettingId) {
      try {
        await this.loadPaySettings(true);
        if (this.currentPaySettingId === paySettingId) {
          if (this.paySettingsTabs.length > 0) {
            this.currentPaySettingId = this.paySettingsTabs[0].id;
          } else {
            this.currentPaySettingId = null;
          }
        }
      } catch (error) {
        const errorMessage = error.response?.data?.message || error
        this.$toasted.error(errorMessage, { duration: 5000 });

      }
    },
    openGeneralSettings() {
      this.activeTab = this.tabsItems[0] || "Earnings";
    }
  },
  watch: {
    $route: {
      handler(newRoute) {
        const { orgPaySettingId } = newRoute.query;
        if (orgPaySettingId) {
          const matchingTab = this.paySettingsTabs.find(tab => tab.id === orgPaySettingId);
          if (matchingTab && this.currentPaySettingId !== orgPaySettingId) {
            this.currentPaySettingId = orgPaySettingId;
            this.$store.commit('SET_PAY_SETTINGS_ID', { key: 'ACTIVE', id: this.currentPaySettingId });
            this.$nextTick(() => {
              this.tabKey += 1;
            });
          }
        }

        // Then set activeTab based on payType
        if (newRoute.query.payType === "independent-contractor") {
          const independentContractorTab = this.paySettingsTabs.find(tab => tab.name === 'Independent Contractor');
          if (independentContractorTab && this.currentPaySettingId !== independentContractorTab.id) {
            this.currentPaySettingId = independentContractorTab.id;
            this.$store.commit('SET_PAY_SETTINGS_ID', { key: 'ACTIVE', id: this.currentPaySettingId });
            this.activeTab = 'Earnings';
            this.$nextTick(() => {
              this.tabKey += 1;
            });
          } else if (independentContractorTab) {
            // Already selected, just set activeTab
            this.activeTab = 'Earnings';
          }
        } else if (newRoute.query.payType === "Pay Schedule") {
          this.activeTab = "Pay Schedule";
        } else if (newRoute.query.payType === "Earnings") {
          this.activeTab = "Earnings";
        } else if (newRoute.query.payType === "Deductions") {
          this.activeTab = "Deductions";
        } else if (newRoute.query.payType === "General") {
          this.activeTab = "General Settings";
        } else if (!newRoute.query.payType || newRoute.query.payType === null) {
          this.activeTab = this.tabsItems[0] || "Earnings";
          this.$nextTick(() => {
            this.tabKey += 1;
          });
        }
      },
      immediate: true
    },
    currentPaySettingId(newVal, oldVal) {
      // Only reset active tab if this is a user-initiated change (not during state preservation)
      // We check if oldVal is null to avoid resetting during initial load or state restoration
      if (newVal && newVal !== oldVal && oldVal !== null && !this.loadingPaySettings) {
        this.activeTab = this.tabsItems[0] || "Earnings";
      }
    }
  },
  async mounted() {
    try {
      this.$handlePrivilege(
         "performance",
        "accessPerformanceTemplateSettings"
      );
      await this.loadPaySettings();
    } catch (error) {
      this.errorPage = true;
    }
  },
};
</script>

<style>
.border__dashed {
  height: 0px;
  margin: 18px 0px;
  border: 1px dashed #878e99;
}
</style>
